/**
 * @file money.ts
 *
 * Money conversion between the commerce-engine `/v1` wire shape ({@link MoneyV1} —
 * an integer amount in the smallest currency unit, plus an ISO-4217 code) and the
 * decimal string the WooCommerce-style admin screens display. `/v1` amounts are
 * minor units (e.g. cents), so {@link fromMoney} divides by 100 and fixes two
 * decimals, matching the two-decimal currencies the shop runs in (e.g. EUR, USD).
 * The inverse `toMoney` builds the wire object from a user-entered price string,
 * tolerating a comma or dot decimal separator, and stamps the shop currency.
 */

import type { MoneyV1 } from './v1-types';

/** Minor units per major unit for the two-decimal currencies the shop supports. */
const MINOR_UNITS_PER_MAJOR = 100;

/**
 * Parses a user-entered decimal, tolerating a comma or dot separator.
 *
 * @param value - The raw input (e.g. `"19,99"` or `"0.5"`).
 *
 * @returns The numeric value, or `null` when empty or unparseable.
 */
export function parseDecimal(value: string): number | null {
  const trimmed = value.trim().replace(/\s/g, '');
  if (trimmed === '') {
    return null;
  }
  const normalized =
    trimmed.includes(',') && !trimmed.includes('.')
      ? trimmed.replace(',', '.')
      : trimmed.replace(/,/g, '');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

/**
 * Formats an integer minor-unit amount as a fixed two-decimal string. Shared by
 * {@link fromMoney} and the coupon money fields (which carry a bare minor-unit
 * integer, not a {@link MoneyV1}).
 *
 * @param amount - The amount in minor units (e.g. cents).
 *
 * @returns The amount as a fixed two-decimal string, e.g. `"19.99"`.
 */
export function minorUnitsToDecimal(amount: number): string {
  return (amount / MINOR_UNITS_PER_MAJOR).toFixed(2);
}

/**
 * Formats a `/v1` money object as the decimal string the screens display.
 *
 * @param money - The `{ amount, currency }` value (amount in minor units).
 *
 * @returns The amount as a fixed two-decimal string, e.g. `"19.99"`.
 */
export function fromMoney(money: MoneyV1): string {
  return minorUnitsToDecimal(money.amount);
}

/**
 * Builds a `/v1` money object from a user-entered price string and the shop
 * currency. An empty or unparseable amount becomes `0`.
 *
 * @param value - The price string (comma or dot decimal).
 * @param currency - The shop's ISO-4217 currency code.
 *
 * @returns The `{ amount, currency }` value with the amount in minor units.
 */
export function toMoney(value: string, currency: string): MoneyV1 {
  const parsed = parseDecimal(value);
  const amount = parsed === null ? 0 : Math.round(parsed * MINOR_UNITS_PER_MAJOR);
  return { amount, currency };
}
